/* Mobile bottom tab bar (< sm). Five slots: four primary destinations + "More" sheet with the rest. */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Vault, Landmark, PieChart, Rocket, Menu, X, LogOut, UserRound, Map, Baby, Package, TrendingUp, Users, Activity, LifeBuoy, ShieldCheck } from "lucide-react";

const ICONS = {
  "/dashboard": Vault, "/banking": Landmark, "/budget": PieChart, "/level-up": Rocket, "/map": Map, "/baby-funds": Baby,
  "/inventory": Package, "/networth": TrendingUp, "/beneficiaries": Users, "/activity": Activity, "/support": LifeBuoy, "/admin": ShieldCheck,
};
const PRIMARY = ["/dashboard", "/banking", "/budget", "/level-up"];

export default function MobileTabBar({ items }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add("has-tabbar");
    return () => document.body.classList.remove("has-tabbar");
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const isActive = (to) => loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
  const primary = PRIMARY.map((to) => items.find((i) => i.to === to)).filter(Boolean);
  const more = items.filter((i) => !PRIMARY.includes(i.to));
  const moreActive = more.some((i) => isActive(i.to)) || loc.pathname.startsWith("/profile");

  return createPortal(
    <>
      <nav className="sm:hidden fixed inset-x-0 bottom-0 z-[46] bg-cream/95 backdrop-blur-xl border-t border-sand pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tabbar" aria-label="Primary">
        <div className="grid grid-cols-5">
          {primary.map((n) => {
            const Icon = ICONS[n.to] || Vault; const active = isActive(n.to);
            return (
              <Link key={n.to} to={n.to} className={`flex flex-col items-center justify-center gap-0.5 h-14 text-[10px] font-semibold tracking-wide transition-colors ${active ? "text-forest" : "text-sage"}`} aria-current={active ? "page" : undefined} data-testid={`tab-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className={`h-7 w-11 rounded-full flex items-center justify-center transition-colors ${active ? "bg-forest/10" : ""}`}><Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.2 : 1.7} /></span>
                {n.label}
              </Link>
            );
          })}
          <button type="button" onClick={() => setOpen((v) => !v)} className={`flex flex-col items-center justify-center gap-0.5 h-14 text-[10px] font-semibold tracking-wide ${moreActive || open ? "text-forest" : "text-sage"}`} aria-expanded={open} data-testid="tab-more">
            <span className={`h-7 w-11 rounded-full flex items-center justify-center ${moreActive || open ? "bg-forest/10" : ""}`}>{open ? <X className="w-[18px] h-[18px]" /> : <Menu className="w-[18px] h-[18px]" strokeWidth={1.7} />}</span>
            More
          </button>
        </div>
      </nav>

      {open && (
        <div className="sm:hidden fixed inset-0 z-[45] bg-ink/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="mobile-more-backdrop">
          <div className="absolute inset-x-0 bottom-14 bg-cream rounded-t-3xl border-t border-sand p-4 pb-6 shadow-2xl animate-in slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()} data-testid="mobile-more-sheet">
            <div className="h-1 w-10 rounded-full bg-sand mx-auto mb-3" />
            <div className="grid grid-cols-3 gap-2">
              {more.map((n) => {
                const Icon = ICONS[n.to] || Vault; const active = isActive(n.to);
                return (
                  <Link key={n.to} to={n.to} className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-3 text-[11px] font-medium text-center ${active ? "bg-forest text-white border-forest" : "bg-white border-sand text-ink"}`} data-testid={`more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.7} /> {n.label}
                  </Link>
                );
              })}
              <Link to="/profile" className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-3 text-[11px] font-medium ${loc.pathname.startsWith("/profile") ? "bg-forest text-white border-forest" : "bg-white border-sand text-ink"}`} data-testid="more-profile">
                <UserRound className="w-5 h-5" strokeWidth={1.7} /> Profile
              </Link>
            </div>
            <button onClick={() => { logout(); nav("/"); }} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-sand bg-white py-3 text-sm text-moss" data-testid="more-signout">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out{user?.email ? ` · ${user.email}` : ""}
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
